import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BriefcaseBusiness, Mail, Phone, UserSquare2 } from "lucide-react";

import AdminLayout from "@/components/layout/AdminLayout";
import GlobalAdminHeader from "@/components/layout/GlobalAdminHeader";
import AppErrorState from "@/components/ui/AppErrorState";
import AppLoadingState from "@/components/ui/AppLoadingState";
import { getApiErrorMessage } from "@/lib/api";
import { listCollaborators, getPilotageCockpit } from "@/lib/pilotageApi";
import { normalizeCollaboratorsRevenueRows } from "@/lib/pilotageCollaborators";
import { formatMoney } from "@/lib/businessRules";
import {
  formatPct0,
  calculateSeniority,
  getResourceTypeLabel,
  getContractKindLabel,
  getCollaboratorStatusLabel,
  formatDateLabel,
} from "@/lib/pilotageUi";

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().substring(0, 2);
};

const CollaboratorDetailPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collaborator, setCollaborator] = useState(null);
  const [metrics, setMetrics] = useState({ revenue: 0, marginPct: 0 });
  const [missions, setMissions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const year = new Date().getFullYear();
        // Fecth both lists to construct the MVP details view
        const [collabs, cockpit] = await Promise.all([
          listCollaborators(),
          getPilotageCockpit({ year })
        ]);

        const collab = collabs.find((c) => String(c.id) === String(id));
        if (!collab) {
           setError("Collaborateur introuvable.");
           setLoading(false);
           return;
        }

        setCollaborator(collab);

        const cockpitMissions = Array.isArray(cockpit?.tables?.missions) ? cockpit.tables.missions : [];
        const collabMissions = cockpitMissions.filter(m => String(m.collaborator_id) === String(id));
        
        // Active and ended missions related to this collab
        setMissions(collabMissions);

        // Find financial metrics for this collaborator
        const revenueRows = normalizeCollaboratorsRevenueRows(cockpit?.tables?.collaborators_revenue);
        const myRevenue = revenueRows.find(r => String(r.collaborator_id) === String(id));
        
        if (myRevenue) {
            setMetrics({
                revenue: Number(myRevenue.caMensuelEstime) || 0,
                revenueAnnual: Number(myRevenue.caAnnuelEstime) || 0,
                marginPct: Number(myRevenue.margePct) || 0,
            });
        }
      } catch (err) {
        setError(getApiErrorMessage(err, "Impossible de charger les détails du collaborateur."));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <AdminLayout header={<GlobalAdminHeader />}>
        <AppLoadingState title="Chargement du dossier" description="Récupération des informations..." />
      </AdminLayout>
    );
  }

  if (error || !collaborator) {
    return (
      <AdminLayout header={<GlobalAdminHeader />}>
        <AppErrorState title="Erreur" message={error || "Collaborateur introuvable"} />
      </AdminLayout>
    );
  }

  const activeMissions = missions.filter(m => m.status === 'ACTIVE');

  return (
    <AdminLayout header={<GlobalAdminHeader />}>
      <div className="w-full max-w-5xl mx-auto space-y-6">
        
        {/* Breadcrumb & Navigation */}
        <div className="flex items-center gap-4 text-sm text-slate-400 mb-6">
          <Link to="/admin/pilotage/collaborateurs" className="hover:text-white transition-colors flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" />
            Retour à la liste
          </Link>
        </div>

        {/* Profile Header */}
        <section className="rounded-3xl border border-white/10 glass-heavy p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-lenvia-blue/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
          
          <div className="flex flex-col md:flex-row gap-8 items-start relative z-10">
            <div className="flex-shrink-0">
               <div className="h-24 w-24 rounded-3xl bg-slate-800 border-2 border-slate-700 flex items-center justify-center text-3xl font-bold text-slate-300 shadow-xl">
                 {getInitials(collaborator.full_name || collaborator.email)}
               </div>
            </div>
            
            <div className="flex-grow">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-3xl font-bold text-white">{collaborator.full_name}</h1>
                <span className="px-3 py-1 rounded-full bg-lenvia-blue/10 text-lenvia-blue text-xs font-semibold tracking-wider font-mono">
                  {getContractKindLabel(collaborator.contract_kind)}
                </span>
                <span className="px-3 py-1 rounded-full bg-white/5 text-slate-300 text-xs font-semibold tracking-wider font-mono">
                  {getResourceTypeLabel(collaborator.contract_kind)}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wider ${
                  collaborator.status === "ACTIVE"
                    ? "bg-emerald-500/10 text-emerald-400"
                    : collaborator.status === "RECRUITMENT"
                    ? "bg-blue-500/10 text-blue-400"
                    : collaborator.status === "PLANNED"
                    ? "bg-amber-500/10 text-amber-400"
                    : collaborator.status === "NOTICE"
                    ? "bg-orange-500/10 text-orange-400"
                    : "bg-slate-800 text-slate-400"
                }`}>
                  {getCollaboratorStatusLabel(collaborator.status)}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wider ${activeMissions.length > 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
                  {activeMissions.length > 0 ? 'EN MISSION' : 'INTERCONTRAT'}
                </span>
              </div>
              
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
                <div className="flex items-center gap-2 text-slate-300">
                  <UserSquare2 className="h-4 w-4 text-slate-500" />
                  <span className="text-sm">{collaborator.title || 'Consultant'}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <a href={`mailto:${collaborator.email}`} className="text-sm hover:text-white transition-colors">{collaborator.email}</a>
                </div>
                {collaborator.phone && (
                   <div className="flex items-center gap-2 text-slate-300">
                     <Phone className="h-4 w-4 text-slate-500" />
                     <span className="text-sm">{collaborator.phone}</span>
                   </div>
                )}
                <div className="flex items-center gap-2 text-slate-300">
                  <span className="text-xs text-slate-500">Entrée :</span>
                  <span className="text-sm font-medium text-slate-200">{formatDateLabel(collaborator.employment_start_date)}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <span className="text-xs text-slate-500">Sortie :</span>
                  <span className="text-sm font-medium text-slate-200">
                    {collaborator.employment_end_date ? formatDateLabel(collaborator.employment_end_date) : "—"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <span className="text-xs text-slate-500">Ancienneté :</span>
                  <span className="text-sm font-medium text-slate-200">
                    {calculateSeniority(collaborator.employment_start_date, collaborator.employment_end_date)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Rapid Financials */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
            <p className="text-xs font-semibold uppercase text-slate-500">CA Mensuel (Estimé)</p>
            <p className="mt-2 text-2xl font-bold text-white">{formatMoney(metrics.revenue)}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
            <p className="text-xs font-semibold uppercase text-slate-500">Marge Générée</p>
            <p className="mt-2 text-2xl font-bold text-white">{formatPct0(metrics.marginPct)}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
            <p className="text-xs font-semibold uppercase text-slate-500">Missions Actives</p>
            <p className="mt-2 text-2xl font-bold text-white">{activeMissions.length}</p>
          </div>
        </div>

        {/* Missions View */}
        <section className="rounded-3xl border border-white/10 glass p-6">
          <div className="flex items-center gap-2 mb-6">
            <BriefcaseBusiness className="h-5 w-5 text-lenvia-blue" />
            <h2 className="text-lg font-bold text-white">Missions & Affectations</h2>
          </div>

          {missions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-400 text-sm">Aucune mission assignée pour l'année en cours.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {missions.map(mission => (
                <div key={mission.id} className={`rounded-xl border p-4 ${mission.status === 'ACTIVE' ? 'border-lenvia-blue/30 bg-lenvia-blue/5' : 'border-white/10 bg-slate-900/50'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-white">{mission.client_name || 'Projet Interne'}</h3>
                      <p className="text-sm text-slate-400 mt-1">{mission.role_title || 'Consultant'}</p>
                    </div>
                    <span className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-400 font-medium">
                      {mission.status}
                    </span>
                  </div>
                  <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                     <div>Début: <span className="font-medium text-slate-300">{mission.start_date || 'NC'}</span></div>
                     {mission.end_date && <div>Fin: <span className="font-medium text-slate-300">{mission.end_date}</span></div>}
                     {mission.days_per_month && <div><span className="font-medium text-slate-300">{mission.days_per_month}</span> j / mois</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

      </div>
    </AdminLayout>
  );
};

export default CollaboratorDetailPage;
